import { useState, type FormEvent } from 'react';
import { adminApi } from '@/features/admin/api/adminApi';
import { Button } from '@/shared/components/Button';
import { ApiError } from '@/shared/api/httpClient';

function generateStrongPassword(): string {
  // Not cryptographically precious -- just needs to clear the 10-char
  // minimum and be easy to read aloud/hand off to the staff member.
  const bytes = crypto.getRandomValues(new Uint8Array(9));
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, '').slice(0, 12);
}

export function ResetPasswordForm({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmedValue, setConfirmedValue] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.users.resetPassword(userId, newPassword);
      setConfirmedValue(newPassword);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not reset password.');
    } finally {
      setIsSaving(false);
    }
  }

  if (confirmedValue) {
    return (
      <div style={{ padding: 'var(--space-3) 0' }}>
        <p style={{ margin: '0 0 8px' }}>
          ✓ Password reset. All existing sessions for this account have been signed out. New password:
        </p>
        <p className="font-mono" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>
          {confirmedValue}
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: '4px 0 12px' }}>
          Make sure to pass this to the staff member now — it isn't stored or shown anywhere else.
        </p>
        <Button variant="secondary" onClick={onDone}>
          Done
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', padding: 'var(--space-3) 0', flexWrap: 'wrap' }}>
      {error && <div className="error-banner" style={{ flexBasis: '100%' }}>{error}</div>}
      <div className="field" style={{ flex: 1, minWidth: 200 }}>
        <label className="field__label">New password</label>
        <input
          type="text"
          className="field__input"
          required
          minLength={10}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </div>
      <Button type="button" variant="secondary" onClick={() => setNewPassword(generateStrongPassword())}>
        Generate
      </Button>
      <Button type="submit" variant="danger" isLoading={isSaving}>
        Reset password
      </Button>
      <Button type="button" variant="ghost" onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
}